import React, { useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";

import {
  Alert,
  Button,
  Card,
  CardStackSection,
  Heading,
  MarkdownLite,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { required } from "@origin-digital/validation";
import { reportException } from "@origin-digital/reporting-client";
import {
  Address,
  AddressLookup,
  AddressResult,
  QasLayout,
  qasLookup,
  QasProfile,
} from "@origin-digital/address-lookup";

import { config } from "src/config";
import { useTrackingContext } from "src/context/TrackingContext";
import { pageRoutes } from "src/consts";
import { reduceValidators } from "src/utils/helpers";
import { errorTracking, formTracking } from "src/utils/tracking";
import {
  hideNotification,
  showErrorNotification,
} from "src/utils/notifications";
import { Form } from "src/components/Form";
import { FieldUpdateHook } from "../../../crm/profileFieldMap";
import { ProfileFieldMapTypes } from "../../../crm/profileFieldMapContextHelpers";
import { generateMailto } from "./helpers";
import content from "./content.json";

export type AddressProp = {
  // current usages of address in app ignore null
  // if anything actually requires null, we can update type and update usages to deal with null
  // LPG and Kraken types for address are nullable but are ignoring at conversion
  address: string;
  id: string;
};

export interface IProps {
  addresses: AddressProp[];
  useUpdate: FieldUpdateHook<Address>;
  type: ProfileFieldMapTypes;
}

export const UpdateAddressScreen: React.FC<IProps> = ({
  addresses,
  useUpdate,
  type,
}) => {
  const [updateAddress, { success, loading, error }] = useUpdate();

  const trackingContext = useTrackingContext();

  const [redirecting, setRedirecting] = useState<boolean>(false);

  const [autocompleteValue, setAutocompleteValue] = useState<string>("");
  const [autocompleteError, setAutocompleteError] = useState<string>("");

  const [address, setAddress] = useState<Address>();

  const { fetchAddresses, fetchAddressDetails } = qasLookup(
    config.qasAuthToken!,
    QasProfile.AUS,
    QasLayout.Basic,
    config.qasEndpoint
  );

  useEffect(() => {
    if (error) {
      showErrorNotification(content.error);
      errorTracking(error, type);
      reportException("UpdateAddressScreen.updateAddress()", error);
    }
  }, [error]);

  useEffect(() => {
    if (success && !error) {
      formTracking("complete", trackingContext, type);
      setRedirecting(true);
    }
  }, [success, error]);

  const alertText = useMemo(
    () => generateMailto(addresses, content.info),
    [addresses]
  );

  const handleValidate = (): string => {
    const { errors } = content.address;

    const validators = [required({ error: errors.required })];

    const errorMessage = reduceValidators(address?.postcode || "", validators);

    setAutocompleteError(errorMessage);

    return errorMessage;
  };

  const handleSelect = async (addressResult: AddressResult | null) => {
    if (addressResult === null) {
      return;
    }
    const { addressId, label } = addressResult;
    const qasAddress = await fetchAddressDetails(addressId);

    setAutocompleteValue(label);
    setAddress(qasAddress);
    setAutocompleteError("");
  };

  const handleError = () => {
    showErrorNotification(content.error);
  };

  const handleQueryChange = (newValue: string) => {
    formTracking("start", trackingContext, type);
    setAutocompleteValue(newValue);
    setAddress(undefined);
  };

  const handleSubmit = () => {
    formTracking("start", trackingContext, type);

    if (handleValidate()) {
      return;
    }

    hideNotification();

    updateAddress(address!);
  };

  if (redirecting) {
    return <Redirect push to={pageRoutes.updateAddress.success} />;
  }

  return (
    <CardStackSection data-id="update-address">
      <Card>
        <Form onSubmit={handleSubmit}>
          <Stack space={["large", "xlarge"]}>
            <Heading data-id="heading" variant="h3">
              {content.heading}
            </Heading>
            <Stack space={["medium", "large"]}>
              {addresses!.length > 1 && (
                <Alert data-id="info-message">
                  <Text>
                    <MarkdownLite>{alertText}</MarkdownLite>
                  </Text>
                </Alert>
              )}
              <AddressLookup
                id="address"
                fetchAddresses={fetchAddresses}
                onSelect={handleSelect}
                onQueryChange={handleQueryChange}
                onError={handleError}
                value={autocompleteValue}
                label={content.address.label}
                helperText={autocompleteError || content.address.helperText}
                error={!!autocompleteError}
                disabled={loading}
              />
            </Stack>
            <Button data-id="submit" fullWidth type="submit" spinner={loading}>
              {content.submit}
            </Button>
          </Stack>
        </Form>
      </Card>
    </CardStackSection>
  );
};
